<template>
    <div>
        <Portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex justify-start">
                <VcInput
                    v-model="searchQuery"
                    prefix-icon="Search"
                    :placeholder="String(t('general.search'))"
                    size="mini"
                    class="mx-2"
                />
            </div>
        </Portal>
        <redesign-data-table-component
            v-if="searchedTableData.length && filteredColumns.length"
            :widget-data="data"
            :columns-config="columnsConfig"
            :columns="filteredColumns"
            :table-data="searchedTableData"
            :row-key="getMultiQueueElTableRowKeyAsRows"
            :stripe="true"
            :style="tableStatusesColorsConfig"
            :default-available-columns="currentDefaultVisibleColumns"
            fix-first-column
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <div :style="{color: column.textColor}">
                    <div
                        v-if="column.prop === multiQueueColumnsPropNames.CALLERS"
                    >
                        <redesign-callers-count
                            :class="getCallersStatusClass(row.queue_id)"
                            :queue-id="row.queue_id"
                            :queue-rule-id="row.queue_rule_id"
                            :queue-name="row.queue_name"
                            :warning-value="maximumWarningCallersValueFromSettings"
                            :filtered-queues="filteredQueues"
                            :limit-value="maximumLimitCallersValueFromSettings"
                            @update-callers-value="updateMaxCallerCount"
                            @clear-queue-max-callers="removeQueueFromMaxCallers"
                        />
                    </div>
                    <div
                        v-else-if="column.prop === multiQueueColumnsPropNames.CURRENT_WAIT_TIME"
                    >
                        <redesign-max-wait-time
                            :class="getWaitTimeStatusClass(row.queue_id)"
                            :queue-id="row.queue_id"
                            :queue-rule-id="row.queue_rule_id"
                            :queue-name="row.queue_name"
                            :limit-value="maximumLimitWaitTimeValueFromSettings"
                            :warning-value="maximumWarningWaitTimeValueFromSettings"
                            :filtered-queues="filteredQueues"
                            @update-wait-time-value="updateCurrentMaxWaitTime"
                            @clear-calls="removeQueueFromMaxWaitTime"
                        />
                    </div>
                    <div
                        v-else-if="column.prop === multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS"
                    >
                        <redesign-active-queue-calls
                            :queue-id="row.queue_id"
                            :table-data="tableDataMergedWithRequest"
                        />
                    </div>
                    <RedesignAgentsCountCell
                        v-else-if="column.prop === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE"
                        :queue-id="row.queue_id"
                        :available="true"
                        :agents="agents"
                    />
                    <RedesignAgentsCountCell
                        v-else-if="column.prop === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE"
                        :queue-id="row.queue_id"
                        :available="false"
                        :agents="agents"
                    />
                    <span v-else>
                        {{ isTimeProp(column.prop) ? timeFormatter(row[column.prop]) : row[column.prop] }}
                    </span>
                </div>
            </template>
        </redesign-data-table-component>
    </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref, toRef, toRefs, watch } from 'vue'
import { timeFormatter } from '@/helpers/timeFormatter'
import { ALL_QUEUE_ID } from '@/enum/redesignQueueDashboardStatistics'
import { useI18n } from 'vue-i18n'
import {
    defaultAvailableColumns,
    defaultRowData,
    defaultVisibleColumns,
    FilteredMultiQueueColumn,
    multiQueueColumnsPropNames,
    QueueData
} from '@/helpers/multiQueueDashboardRedesign'
import { getMultiQueueElTableRowKeyAsRows, useMultiQueueTableCommon } from '@/composables/useMultiQueueTableCommon'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import RedesignCallersCount from '@/components/RedesignComponents/Queue/RedesignCallersCount.vue'
import RedesignMaxWaitTime from '@/components/RedesignComponents/Queue/RedesignMaxWaitTime.vue'
import { ColumnsObjectConfig, TableHeadersConfig, WidgetExtended } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { VcInput } from '@voicenter-team/voicenter-ui-plus'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import RedesignActiveQueueCalls from '@/components/RedesignComponents/Queue/RedesignActiveQueueCalls.vue'
import RedesignAgentsCountCell from '@/components/RedesignComponents/Queue/RedesignAgentsCountCell.vue'
import { useQueueAgentsInline } from '@/composables/useQueueAgentsInline'
import { getTranslationOrUndefined } from '@/helpers/getTranslation'
import startCase from 'lodash/startCase'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'
import { isMultiQueueRulesDashboard } from '@/helpers/widgetUtils.js'
import TagsCachedDataService from '@/core/services/cachedData/TagsCachedDataService'

const { t } = useI18n()

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void
}>()

const props = defineProps<{
    data: WidgetExtended
    queueTableData: QueueData[]
}>()

const { data: widgetData } = toRefs(props)
const searchQuery = ref('')
const columnsConfig = ref<ColumnsObjectConfig>({})

const isRulesMode = computed(() => isMultiQueueRulesDashboard(props.data))

const displayRowWithTotal = computed(() => {
    return props.data.WidgetLayout?.displayRowWithTotals ?? true
})
const showStatsInPercentage = computed(() => {
    return props.data.WidgetLayout?.showStatsInPercentage ?? false
})

const {
    mappedBaseTableData,
    filteredQueues,
    maximumWarningCallersValueFromSettings,
    maximumLimitCallersValueFromSettings,
    maximumWarningWaitTimeValueFromSettings,
    maximumLimitWaitTimeValueFromSettings,
    updateMaxCallerCount,
    removeQueueFromMaxCallers,
    updateCurrentMaxWaitTime,
    removeQueueFromMaxWaitTime,
    getCallersStatusClass,
    getWaitTimeStatusClass,
    getColumnTextColor
} = useMultiQueueTableCommon(
    widgetData,
    toRef(props, 'queueTableData'),
    showStatsInPercentage,
    isRulesMode
)

const { getAgents } = useQueueAgentsInline()

const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()

const agents = computed(() => {
    return getAgents() || {
        allAvailable: [],
        allUnavailable: []
    }
})

const columns = computed(() => {
    void TagsCachedDataService.dataRef.value

    const baseColumns = [ ...defaultAvailableColumns ]

    if (isRulesMode.value) {
        const queueNameIndex = baseColumns.findIndex(c => c.prop === multiQueueColumnsPropNames.QUEUE_NAME)
        baseColumns.splice(queueNameIndex + 1, 0, {
            prop: 'queue_rule_name',
            align: 'center',
            fixed: false,
            label: 'Queue Rule Name',
            minWidth: 130,
        })
    }

    return baseColumns.map((column) => {
        const currentColumn = props.data?.WidgetTemplateColumns?.find(
            (col) => column.prop === col.WidgetTemplateColumnsName
        )

        if (!currentColumn) return column

        const label = getTranslationOrUndefined(currentColumn.TemplateNameContentTag)
            || getTranslationOrUndefined(`WidgetTemplateColumnName.${currentColumn.WidgetTemplateColumnsName}`)
            || getTranslationOrUndefined(currentColumn.WidgetTemplateColumnsName)
            || startCase(currentColumn.WidgetTemplateColumnsName)

        if (label) {
            column.label = label
        }

        return column
    })
})

const filteredColumns = computed<FilteredMultiQueueColumn[]>(() =>
    columns.value.map((column) => ({
        ...column,
        label: makeTableHeaderTranslations(column, column.label),
        tooltipLabel: makeTableHeaderTooltipTranslations(column, column.label),
        textColor: getColumnTextColor(column.prop),
    }))
)

const currentDefaultVisibleColumns = computed(() => {
    if (isRulesMode.value) {
        return {
            ...defaultVisibleColumns,
            queue_rule_name: true
        }
    }
    return defaultVisibleColumns
})

const tableDataMergedWithRequest = computed(() => {
    const maxRingTime: number[] = []
    const averageRingTime: number[] = []
    let totalCallsCount = 0
    const totalData: Record<string, string | number> = {
        ...defaultRowData,
        queue_id: ALL_QUEUE_ID,
        queue_name: ALL_QUEUE_ID
    }

    Object.values(mappedBaseTableData.value).forEach((queueData) => {
        const queueDataFromApi = props.queueTableData.find((data) => queueData.queue_id === data.queue_id)
        const CallCount = queueDataFromApi?.[multiQueueColumnsPropNames.CALL_COUNT]

        if (typeof CallCount === 'number') {
            totalCallsCount += CallCount
        }

        const maxRingTimeValue = queueData[multiQueueColumnsPropNames.MAX_RING_TIME]
        if (typeof maxRingTimeValue === 'number') {
            maxRingTime.push(maxRingTimeValue)
        }

        const avgRingTimeValue = queueData[multiQueueColumnsPropNames.AVG_RING_TIME]
        if (typeof avgRingTimeValue === 'number' && avgRingTimeValue && typeof CallCount === 'number') {
            averageRingTime.push(avgRingTimeValue * CallCount)
        }

        Object.keys(queueData).forEach(key => {
            if (key !== 'queue_id' && key !== 'queue_name') {
                const value = queueData[key]
                if (typeof value === 'number' && typeof totalData[key] === 'number') {
                    totalData[key] += value
                }
            }
        })
    })

    if (maxRingTime.length) {
        totalData[multiQueueColumnsPropNames.MAX_RING_TIME] = Math.max(...maxRingTime)
    }

    if (averageRingTime.length) {
        const totalRingTime = averageRingTime.reduce((acc, value) => {
            acc += value
            return acc
        }, 0)
        totalData[multiQueueColumnsPropNames.AVG_RING_TIME] = Math.ceil(totalRingTime / totalCallsCount)
    }
    totalData[multiQueueColumnsPropNames.CALL_COUNT] = totalCallsCount

    const avgAnswerHourly = totalData[multiQueueColumnsPropNames.AVG_ANSWER_HOURLY]
    if (typeof avgAnswerHourly === 'number') {
        totalData[multiQueueColumnsPropNames.AVG_ANSWER_HOURLY] = parseFloat(avgAnswerHourly.toFixed(2))
    }

    const allData = Object.values(mappedBaseTableData.value)

    if (displayRowWithTotal.value) {
        allData.push(totalData)
    }

    return sortTableDataByDefaultList(allData, props.data, 'queue_id')
})

const searchedTableData = computed(() => {
    if (!tableDataMergedWithRequest.value.length) {
        return tableDataMergedWithRequest.value
    }
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return tableDataMergedWithRequest.value

    return tableDataMergedWithRequest.value.filter(item =>
        Object.values(item).some(val =>
            String(val).toLowerCase().includes(query)
        )
    )
})

const maximumWarningCallersColor = computed(() => props.data?.WidgetLayout?.maximumWarningCallersColor || '')
const maximumLimitCallersColor = computed(() => props.data?.WidgetLayout?.maximumLimitCallersColor || '')
const maximumWarningMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumWarningMaxRingTimeColor || '')
const maximumLimitMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumLimitMaxRingTimeColor || '')
const tableStatusesColorsConfig = computed(() => {
    return {
        '--maximum-warning-callers-color': maximumWarningCallersColor.value,
        '--maximum-limit-callers-color': maximumLimitCallersColor.value,
        '--maximum-warning-max-ring-time-color': maximumWarningMaxRingTimeColor.value,
        '--maximum-limit-max-ring-time-color': maximumLimitMaxRingTimeColor.value
    }
})

const isTimeProp = (prop: string) => {
    return prop === multiQueueColumnsPropNames.MAX_RING_TIME || prop === multiQueueColumnsPropNames.AVG_RING_TIME
}

const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config) {
        return
    }
    columnsConfig.value = config

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsAsQueuesConfig: config
        }
    }
    emit('update-widget', updatedWidgetData)
}

const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    if (config) {
        columnsConfig.value = config
    }

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config ? { columnsAsQueuesConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}

const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}

watch(() => props.data.WidgetLayout, (val) => {
    if (val) {
        columnsConfig.value = val?.columnsAsQueuesConfig ?? {}
    }
}, {
    immediate: true,
})
</script>

<style scoped lang="scss">
</style>

